import { useEffect, useState } from "react";
import { useReplicache } from "../replicache/context";

// Polls /version.json (bypassed by the service worker) and shows a small
// "new version available" chip when the deployed bundle id differs from
// the one this page loaded with.  Auto-reloads after a countdown unless
// the user has pending Replicache mutations — never want to silently
// drop unsynced work.

const POLL_MS = 60_000;
const AUTO_RELOAD_AFTER_S = 30;
// Skip the first check window so a freshly-built dev environment doesn't
// trip the banner mid-iteration before the page has settled.
const STARTUP_GRACE_MS = 5_000;

export function UpdateBanner() {
  const rep = useReplicache();
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [latestId, setLatestId] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  // On mount, record what we're currently running.  Failure (e.g.
  // network blip, missing file in dev) leaves loadedId null which
  // disables the check until the next poll succeeds.  URL is cache-
  // busted with a timestamp because the user's tab may still be
  // controlled by an older service worker (clients.claim() doesn't
  // switch existing controllers) that caches /version.json without
  // bypass — a unique URL each time forces a real network hit.
  useEffect(() => {
    const t = window.setTimeout(async () => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const v = (await r.json()) as { id?: string };
        if (typeof v.id === "string") setLoadedId(v.id);
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  // Poll for a new bundle id.  The polling runs on a wall-clock
  // interval; we also re-check on tab focus so a user returning to the
  // app sees the prompt straight away rather than after up to a minute.
  useEffect(() => {
    if (!loadedId) return;
    let cancelled = false;
    const startedAt = Date.now();

    const check = async () => {
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const v = (await r.json()) as { id?: string };
        if (cancelled || typeof v.id !== "string") return;
        if (Date.now() - startedAt < STARTUP_GRACE_MS) return;
        const fresh: string = v.id;
        setLatestId((prev) => (prev === fresh ? prev : fresh));
      } catch {
        // ignore
      }
    };

    void check();
    const interval = window.setInterval(check, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadedId]);

  const updateAvailable = !!(
    loadedId &&
    latestId &&
    latestId !== loadedId
  );

  // Auto-reload countdown.  Starts when we detect an update; pauses if
  // we see pending Replicache mutations (don't want to wipe out
  // un-pushed local writes silently).  Pages without Replicache in scope
  // (e.g. the public share viewer) just count down — they don't write.
  useEffect(() => {
    if (!updateAvailable) {
      setCountdown(null);
      return;
    }
    let cancelled = false;
    let tickHandle: number | null = null;
    let secondsLeft = AUTO_RELOAD_AFTER_S;

    const tick = async () => {
      if (cancelled) return;
      if (rep) {
        try {
          const pending = await rep.experimentalPendingMutations();
          if (pending.length > 0) {
            setCountdown(null);
            tickHandle = window.setTimeout(tick, 2_000);
            return;
          }
        } catch {
          // If the local store is unhealthy, fall through and count down.
        }
      }
      setCountdown(secondsLeft);
      if (secondsLeft <= 0) {
        window.location.reload();
        return;
      }
      secondsLeft -= 1;
      tickHandle = window.setTimeout(tick, 1_000);
    };

    void tick();
    return () => {
      cancelled = true;
      if (tickHandle !== null) window.clearTimeout(tickHandle);
    };
  }, [updateAvailable, rep]);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed inset-x-0 bottom-3 z-50 mx-auto flex w-fit max-w-[calc(100vw-1rem)] items-center gap-3 rounded-full border border-indigo-200 bg-white px-4 py-2 text-sm text-slate-800 shadow-lg dark:border-indigo-800 dark:bg-slate-900 dark:text-slate-100"
    >
      <span aria-hidden>🔄</span>
      <span>
        {countdown === null
          ? "New version ready (waiting for sync)…"
          : countdown > 0
            ? `New version ready · auto-reload in ${countdown}s`
            : "Reloading…"}
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500"
      >
        Reload now
      </button>
    </div>
  );
}
